import Phaser from 'phaser';

/**
 * Global event bus for communication between game systems
 * Uses Phaser's EventEmitter for consistency with the game framework
 */
class GameEventBus extends Phaser.Events.EventEmitter {
  private static instance: GameEventBus;

  private constructor() {
    super();
  }

  public static getInstance(): GameEventBus {
    if (!GameEventBus.instance) {
      GameEventBus.instance = new GameEventBus();
    }
    return GameEventBus.instance;
  }

  /**
   * Emit an event with type safety
   */
  public emitEvent<T extends keyof GameEvents>(event: T, data: GameEvents[T]): boolean {
    return this.emit(event, data);
  }

  /**
   * Listen for an event with type safety
   */
  public onEvent<T extends keyof GameEvents>(
    event: T,
    callback: (data: GameEvents[T]) => void,
    context?: object
  ): this {
    return this.on(event, callback, context);
  }

  /**
   * Listen for an event once with type safety
   */
  public onceEvent<T extends keyof GameEvents>(
    event: T,
    callback: (data: GameEvents[T]) => void,
    context?: object
  ): this {
    return this.once(event, callback, context);
  }
}

/**
 * Type definitions for all game events
 * Add new events here to get type safety
 */
export interface GameEvents {
  // Player events
  'player:moved': { x: number; y: number; z?: number };
  'player:action': { action: string; target?: string };
  'player:command': { command: string; data?: unknown };
  'player:healed': { amount: number };

  // Beastie events
  'beastie:spawned': { id: string; speciesId: string; position: { x: number; y: number } };
  'beastie:despawned': { id: string };
  'beastie:stateUpdate': { id: string; state: unknown };
  'beastie:damaged': { id: string; damage: number; source?: string };
  'beastie:healed': { id: string; amount: number };
  'beastie:levelUp': { id: string; newLevel: number };

  // Combat events
  'combat:started': { participants: string[] };
  'combat:ended': { winner?: string; participants: string[] };
  'combat:attack': { attackerId: string; targetId: string; damage: number; targetHealthPercent: number };
  'combat:target_defeated': { attackerId: string; targetId: string };
  'combat:ability': { casterId: string; abilityId: string; targets: string[] };

  // Death events
  'entity:died': { entityId: string; killedBy: string; entityType: 'enemy' | 'party' | 'player' };
  'entity:fainted': { entityId: string; speciesId: string };
  'player:died': { position: { x: number; y: number } };
  'game:over': { reason: string };
  'xp:awarded': { recipientId: string; amount: number; sourceId: string };
  'loot:dropped': { position: { x: number; y: number }; items: string[] };

  // Taming events
  'taming:started': { beastieId: string; playerId: string };
  'taming:fed': { beastieId: string; itemId: string; loyaltyGain: number; newProgress: number; threshold: number };
  'taming:progress': { beastieId: string; progress: number; threshold: number };
  'taming:captured': { beastieId: string; speciesId: string };
  'taming:fled': { beastieId: string };
  'taming:failed': { beastieId: string; reason: string };

  // World events
  'world:zoneChanged': { from: string; to: string };
  'world:interacted': { entityId: string; type: string };
  'spawn:point': { name: string; type: string; x: number; y: number; properties: unknown };
  'trigger:register': { name: string; type: string; bounds: unknown; properties: unknown };

  // Tile trigger events
  'tile:trigger:enter': { entityId: string; tileX: number; tileY: number; tileType: number; layer?: string };
  'tile:trigger:exit': { entityId: string; tileX: number; tileY: number };

  // Network events
  'network:connected': { sessionId: string };
  'network:disconnected': { reason: string };
  'network:playerJoined': { sessionId: string; username: string };
  'network:playerLeft': { sessionId: string };

  // UI events
  'ui:menuOpened': { menu: string };
  'ui:menuClosed': { menu: string };
  'ui:notification': { message: string; type: 'info' | 'warning' | 'error' };
  'ui:show_inventory': Record<string, never>;
  'ui:hide_inventory': Record<string, never>;
  'ui:select_target': { filter: 'fainted' | 'damaged' | 'any' };
  'ui:target_selected': { entityId: string };
  'ui:target_cancelled': Record<string, never>;

  // Inventory events
  'inventory:changed': { entityId: string };
  'inventory:use_item': { itemId: string; targetEntityId?: string };
  'inventory:item_used': { itemId: string; targetEntityId: string; effect: string };

  // Revival events
  'beastie:revived': { entityId: string; revivedBy: 'item' | 'shrine'; healthPercent: number };
  'shrine:used': { shrineId: string; revivedCount: number };
  'shrine:nearby': { shrineId: string; inRange: boolean };
}

// Export singleton instance
export const EventBus = GameEventBus.getInstance();
